import { useEffect, useState, useRef, ReactNode } from "react";
import styled from "styled-components";

function Popup({
  openState,
  setOpenState,
  children,
  durationMS,
  closeButton,
  closeColor,
  onClose,
}: {
  openState: boolean;
  setOpenState: (v: boolean) => void;
  children: ReactNode;
  durationMS: number;
  closeButton?: ReactNode;
  closeColor?: string;
  onClose?: () => void;
}) {
  const [visible, setVisible] = useState(false);
  const popupRef = useRef<HTMLDivElement | null>(null);

  function handleRef(node: HTMLDivElement) {
    if (popupRef.current === null) {
      node.style.transitionDuration = `${durationMS}ms`;
      setTimeout(() => {
        node.style.opacity = "1";
        popupRef.current.style.filter = "blur(0px)";
      }, 100);
      popupRef.current = node;
    } else {
      return;
    }
  }

  function handleClose() {
    if (!popupRef.current) return;
    popupRef.current.style.opacity = "0";
    popupRef.current.style.filter = "blur(2px)";
    setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        setOpenState(false);
        onClose && onClose();
        popupRef.current = null;
      }, durationMS);
    }, durationMS);
  }
  
  useEffect(() => {
    if (visible && popupRef.current) {
      popupRef.current.style.filter = "blur(2px)";
      popupRef.current.style.opacity = "0";
      setTimeout(() => {
        setVisible(openState);
        popupRef.current = null;
      }, durationMS + 10);
    } else {
      setVisible(openState);
    }
  }, [openState]);

  return (
    visible && (
      <PopupWrapper ref={(node) => node && handleRef(node)}>
        {children}
        {closeButton ? (
          <Close onClick={handleClose}>{closeButton}</Close>
        ) : (
          <Close onClick={handleClose}>
            <CloseIcon
              strokeWidth={7}
              strokeColor={closeColor ?? "#FFFFFF"}
              width="50%"
            />
          </Close>
        )}
      </PopupWrapper>
    )
  );
}

export default Popup;

const PopupWrapper = styled.div`
  position: fixed;
  top: 0;
  z-index: 10000000000000;
  width: 100%;
  height: 100%;
  opacity: 0;
  filter: blur(2px);
  transition-property: opacity, filter;
  transition-timing-function: ease-out;
`;

const Close = styled.div`
  position: absolute;
  z-index: 1;
  width: clamp(30px, 4rem, 40px);
  aspect-ratio: 1/1;
  cursor: pointer;
  pointer-events: auto;
  bottom: 35px;
  left: 50%;
  display: flex;
  place-content: center;
  border-radius: 100%;
  background-color: var(--secondary);
  border: none;
  transform: translateX(-50%);
  box-shadow: 2px 2px 10px rgba(var(--accent-rgb), 0.4);
`;

type TSVGPropTypes = {
  strokeWidth: number;
  strokeColor: string;
  bgColor?: string;
  [x: string]: any;
};

function CloseIcon({
  strokeWidth,
  strokeColor,
  bgColor,
  ...props
}: TSVGPropTypes) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" {...props}>
      <circle cx="50" cy="50" r="50" fill={bgColor ?? "none"} />
      <line
        x1="5"
        y1="5"
        x2="95"
        y2="95"
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
      />
      <line
        x1="95"
        y1="5"
        x2="5"
        y2="95"
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
      />
    </svg>
  );
}
